import { downloadBinaryResource, showMessageError } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import axios from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { t } from 'ttag';
import {
  convertBinaryToECGData,
  calculateMinutelyHrsDataArray,
  getFirstMinuteSampleLength,
  calculateMinutelyHrsECGData,
  convertViewerObject,
} from '../../lib/eventECGViewer';
import type { EventECGViewerProps } from './eventECGViewer.types';

/**
 * Drives the single-event ECG viewer opened from the study events tab. Loads the event's ECG and full-event data into merged state whenever the event prop changes, caches the header title info in a ref, and exposes the close handler plus the per-minute chart selection and the full-event toggle that downloads and splits the whole event once.
 *
 * @param data - Event with its report data, heart-rate info, timing and validity flags.
 * @param handleClickBack - Callback closing the viewer.
 * @returns Viewer state, the title info ref and the close, full-event toggle and minute-selection handlers.
 */
export const useEventECGViewer = ({ data, handleClickBack }: EventECGViewerProps) => {
  const [state, setState] = useMergeState<any>({
    ecgData: undefined,
    ecgObject: {},
    configChart: {},
    isLoadedData: false,
    isShowViewFullEventButton: false,
    isViewFullEventAvailable: false,
    isShowViewFullEventChart: false,
    isNoEcgData: false,
  });

  const titleInfo = useRef<any>({});
  const isDownLoadedViewFullEventData = useRef(false);

  /**
   * Fetches an event's minutely heart-rate payload from its signed url. Returns the response body, or logs the failure, surfaces it as an error message and returns null.
   *
   * @param url - Signed url of the minutely heart-rate resource.
   * @returns The downloaded payload, or null on failure.
   */
  const downloadMinutelyHrs = async (url: any) => {
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : undefined;
      console.error('Failed to download minutelyHrs: ', message);
      showMessageError({ content: message });
      return null;
    }
  };

  /**
   * Loads one event's ECG samples and full-event metadata into the viewer state. Downloads and de-interleaves the binary ECG plus the minutely heart-rate series, assembles the ECG object from saved review settings when present or from the diagnostic lead's AI prediction at 1000Hz, upgrades version-1 annotations, then stores title info, duration, min and max heart rate and the full-event availability flags, reporting failures as an error message.
   *
   * @returns A promise resolving once the viewer state has been populated.
   */
  const createData = async () => {
    try {
      setState({ isLoadedData: false });

      const {
        eventFid,
        eventType,
        startStudyDate,
        triggerTime,
        timezone,
        facilityCountry,
        facilityTimezone,
        reportData,
        hrInfo,
        diagnosticLead,
        studyTimeOffset,
      } = data;
      const {
        dataPath,
        eventPos,
        channels,
        samplingFrequency,
        gain,
        firstSample,
        fullData,
        aiPredict,
      } = reportData;

      // *: Download ECG data
      const binaryECGs = await downloadBinaryResource(dataPath);
      const ecgData = convertBinaryToECGData(channels?.length, binaryECGs);
      // *: Download minutelyHrs data
      const minutelyHrsDataArray =
        fullData && fullData?.minutelyHrs
          ? calculateMinutelyHrsDataArray(await downloadMinutelyHrs(fullData?.minutelyHrs))
          : [];

      const viewFullEventData = {};
      if (fullData) {
        _.assign(viewFullEventData, {
          firstSample,
          minutelyHrsURL: fullData?.minutelyHrs,
          fullDataURL: fullData?.dataPath,
          minutelyHrsDataArray,
        });
      }

      const ecgObject = {};
      if (ecgData && ecgData[0].length) {
        let beatMarkers = [];
        let hesBeatStatus = [];
        let viewerObject: any = {
          options: {},
          widgets: [],
        };
        let speed;
        let amp;
        let wndDuration;
        let hpFilters;
        let notchFilters;
        let hiddenChannels;
        let isShowBeatMarkers = true;
        let isShowVES = true;
        let isShowSVES = true;

        if (!_.isNil(hrInfo)) {
          if (hrInfo.beatMarkers) {
            beatMarkers = hrInfo.beatMarkers;
          }
          if (hrInfo.viewerObject) {
            // *: Need to convert in order to draw annotations with old data (version 1)
            viewerObject = convertViewerObject(hrInfo.viewerObject, samplingFrequency);
          }
          if (!_.isNil(hrInfo.speed)) {
            speed = hrInfo.speed;
          }
          if (!_.isNil(hrInfo.amp)) {
            amp = hrInfo.amp;
          }
          if (!_.isNil(hrInfo.wndDuration)) {
            wndDuration = hrInfo.wndDuration;
          }
          if (!_.isNil(hrInfo.hpFilters)) {
            hpFilters = hrInfo.hpFilters;
          }
          if (!_.isNil(hrInfo.notchFilters)) {
            notchFilters = hrInfo.notchFilters;
          }
          if (hrInfo.hiddenChannels) {
            hiddenChannels = hrInfo.hiddenChannels;
          }
          if (!_.isNil(hrInfo.isShowBeatMarkers)) {
            isShowBeatMarkers = hrInfo.isShowBeatMarkers;
          }
          if (!_.isNil(hrInfo.hesBeatStatus)) {
            if (!_.isEmpty(hrInfo.beatMarkers) && _.isEmpty(hrInfo.hesBeatStatus)) {
              hesBeatStatus = _.map(hrInfo.beatMarkers, () => 1);
            } else {
              hesBeatStatus = hrInfo.hesBeatStatus;
            }
          }
          if (!_.isNil(hrInfo.isShowVES)) {
            isShowVES = hrInfo.isShowVES;
          }
          if (!_.isNil(hrInfo.isShowSVES)) {
            isShowSVES = hrInfo.isShowSVES;
          }
        } else if (samplingFrequency === 1000) {
          const aiChannelIndex = diagnosticLead - 1;
          /**
           * Selects the AI prediction record belonging to the diagnostic lead's channel. Finds the prediction entry whose channel equals the zero-based diagnostic lead index.
           *
           * @returns The matching AI prediction, or undefined.
           */
          const channelAIData = _.find(aiPredict, (x) => x.channel === aiChannelIndex);
          beatMarkers = channelAIData?.beats || [];
          if (!_.isNil(channelAIData?.hesBeatStatus)) {
            if (!_.isEmpty(channelAIData?.beats) && _.isEmpty(channelAIData?.hesBeatStatus)) {
              hesBeatStatus = _.map(channelAIData.beats, () => 1);
            } else {
              hesBeatStatus = channelAIData.hesBeatStatus;
            }
          }
        }

        _.assign(ecgObject, {
          speed,
          amp,
          wndDuration,
          adcGain: gain,
          adcZero: 0,
          beatMarkers,
          hesBeatStatus,
          hpFilters,
          notchFilters,
          hiddenChannels: hiddenChannels || _.range(channels.length),
          isShowBeatMarkers,
          numChannels: channels.length,
          samplingRate: samplingFrequency,
          viewerObject,
          dataSamples: ecgData,
          eventPos,
          diagnosticLead: diagnosticLead || 1,
          isShowVES,
          isShowSVES,
        });
      }

      titleInfo.current = {
        eventFid,
        eventType,
        startStudyDate,
        triggerTime,
        timezone,
        studyTimeOffset,
        facilityCountry,
        facilityTimezone,
      };

      const postEventTime = data.postEventTime || 0;
      const isShowViewFullEventButton = fullData?.duration > postEventTime;
      const isViewFullEventAvailable =
        data.isValid &&
        !_.isEmpty(fullData) &&
        (!_.isEmpty(fullData?.dataPath) ||
          ((!_.isNil(fullData?.maxHr) || !_.isNil(fullData?.minHr)) && !isShowViewFullEventButton));
      setState({
        ecgData,
        ecgObject,
        configChart: {
          isJumpOffset: true,
          isDisplayScrollbar: true,
          isEdit: true,
        },
        isLoadedData: true,
        viewFullEventData,
        eventDuration: fullData?.duration,
        maxHr:
          fullData?.maxHr === undefined || fullData?.maxHr === null
            ? minutelyHrsDataArray.length
              ? Math.max(...minutelyHrsDataArray) > 0
                ? Math.max(...minutelyHrsDataArray)
                : undefined
              : undefined
            : fullData?.maxHr,
        minHr:
          fullData?.minHr === undefined || fullData?.minHr === null
            ? minutelyHrsDataArray.length
              ? Math.min(...minutelyHrsDataArray) > 0
                ? Math.min(...minutelyHrsDataArray)
                : undefined
              : undefined
            : fullData?.minHr,
        isShowViewFullEventButton,
        isViewFullEventAvailable,
        isAppliedAI: !_.isNil(hrInfo?.isAppliedAIByDefault),
        timeObject: {
          startMoment: dayjs(firstSample).valueOf(),
          timezone: data?.timezone,
        },
        isNoEcgData: _.isEmpty(ecgData) && !(ecgObject as any)?.dataSamples?.length,
      });
    } catch (error) {
      console.error('Failed to fetch event: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    }
  };

  /**
   * Closes the event ECG viewer. Empties the cached title info ref and invokes the back callback supplied by the parent.
   *
   * @returns Nothing.
   */
  const onClickClose = () => {
    titleInfo.current = {};
    handleClickBack();
  };

  /**
   * Switches the ECG viewer between the basic event strip and the full-event chart. Scrolls to the top and, on the first switch, downloads the full binary ECG and the minutely heart-rate file in parallel and splits them into per-minute chunks cached for later toggles, showing a load-failure message when either download is empty; switching back restores the original event samples and event position.
   *
   * @returns A promise resolving once the displayed view has been switched.
   */
  const toggleDisplayViewFullEventChart = async () => {
    // *: Scroll to top after toggling View full event/Basic view button
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });

    const newIsShowViewFullEventChart = !state.isShowViewFullEventChart;

    const clonedEcgObject = _.cloneDeep(state.ecgObject);

    if (newIsShowViewFullEventChart) {
      const { firstSample, minutelyHrsURL, fullDataURL } = state.viewFullEventData;

      if (!isDownLoadedViewFullEventData.current) {
        // *: Download data from URL
        const [fullBinaryData, minutelyHrsData] = await Promise.all([
          downloadBinaryResource(fullDataURL),
          downloadMinutelyHrs(minutelyHrsURL),
        ]);

        if (fullBinaryData && minutelyHrsData) {
          const fullECGData = convertBinaryToECGData(state.ecgObject.numChannels, fullBinaryData);
          const minutelyHrsDataArray = calculateMinutelyHrsDataArray(minutelyHrsData);
          const firstMinuteSampleLength = getFirstMinuteSampleLength(
            firstSample,
            state.ecgObject.samplingRate,
          );
          const { minutelyHrsECGData, missingSampleLength } = calculateMinutelyHrsECGData(
            fullECGData,
            minutelyHrsDataArray.length,
            firstMinuteSampleLength,
            state.ecgObject.samplingRate,
            state.ecgObject.adcGain,
          );

          const clonedViewFullEventData = _.cloneDeep(state.viewFullEventData);
          clonedViewFullEventData.minutelyHrsDataArray = minutelyHrsDataArray;
          clonedViewFullEventData.minutelyHrsECGData = minutelyHrsECGData[0];

          isDownLoadedViewFullEventData.current = true;

          _.assign(clonedEcgObject, {
            beatMarkers: [],
            hesBeatStatus: [],
            dataSamples: minutelyHrsECGData[0],
            eventPos: 0,
          });

          setState({
            viewFullEventData: clonedViewFullEventData,
            minutelyHrsECGData,
            originalMissingSampleLength: missingSampleLength,
            missingSampleLength,
            isShowViewFullEventChart: true,
            ecgObject: clonedEcgObject,
          });
        } else {
          showMessageError({ content: t`Failed to load data` });
        }
      } else {
        const clonedViewFullEventData = _.cloneDeep(state.viewFullEventData);
        clonedViewFullEventData.minutelyHrsECGData = state.minutelyHrsECGData[0];

        _.assign(clonedEcgObject, {
          beatMarkers: [],
          hesBeatStatus: [],
          dataSamples: state.minutelyHrsECGData[0],
          eventPos: 0,
        });

        setState({
          isShowViewFullEventChart: true,
          viewFullEventData: clonedViewFullEventData,
          missingSampleLength: state.originalMissingSampleLength,
          ecgObject: clonedEcgObject,
        });
      }
    } else {
      _.assign(clonedEcgObject, {
        beatMarkers: [],
        hesBeatStatus: [],
        dataSamples: state.ecgData,
        eventPos: data.reportData.eventPos,
      });

      setState({
        isShowViewFullEventChart: false,
        missingSampleLength: 0,
        ecgObject: clonedEcgObject,
      });
    }
  };

  /**
   * Switches the viewer to the minute of the full event picked on the heart-rate chart. Deep-clones the full-event data and the ECG object with that minute's samples, and zeroes the missing-sample length for every minute except the first, which keeps its original value.
   *
   * @param index - Index of the selected minute within the minutely ECG data.
   * @returns Nothing.
   */
  const updateChartData = (index: any) => {
    const clonedViewFullEventData = _.cloneDeep(state.viewFullEventData);
    clonedViewFullEventData.minutelyHrsECGData = state.minutelyHrsECGData[index];

    const clonedEcgObject = _.cloneDeep(state.ecgObject);
    _.assign(clonedEcgObject, { dataSamples: state.minutelyHrsECGData[index] });

    setState({
      viewFullEventData: clonedViewFullEventData,
      missingSampleLength: index !== 0 ? 0 : state.originalMissingSampleLength,
      ecgObject: clonedEcgObject,
    });
  };

  useEffect(() => {
    createData();
  }, [data]);

  return {
    state,
    titleInfo,
    onClickClose,
    toggleDisplayViewFullEventChart,
    updateChartData,
  };
};
